import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PushMessagesDataService, PushMessagesColumnsService } from '../../services';
import { DialogPushMessageFormComponent } from './push-message-form/push-message-form.component';
import { DialogPushMessageDetailsComponent } from './push-message-details/push-message-details.component';
import { PushMessage, PushMessagesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-push-messages',
  templateUrl: './push-messages.component.html',
  styleUrls: ['./push-messages.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PushMessagesComponent implements OnInit, OnDestroy {

  private pushMessageAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private pushMessageEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private pushMessageDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public pushMessageToDelete: PushMessage = {};
  public pushMessages: PushMessage[] = [];
  public pushMessage: PushMessage = {};
  public selectedPushMessages: PushMessage[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private pushMessagesDataService: PushMessagesDataService,
    private pushMessagesColumnsService: PushMessagesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PushMessagesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Push Messages - Management');
    this.primengConfig.ripple = true;
    this.getPushMessages();
  }

  ngOnDestroy(): void {
    console.log('PushMessagesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPushMessages(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.pushMessagesDataService.getPushMessages(this.parameters).subscribe((response: PushMessagesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.pushMessages = response.results;
        } else {
          this.pushMessages = this.pushMessages.concat(response.results);
        }
        if (response.inlineCount > this.pushMessages.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPushMessages();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHMESSAGESNOTLOADED')});
    });
  }

  importPushMessages(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.pushMessagesDataService.addPushMessages(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PUSHMESSAGESCREATED')});
                if (proceed === j) {
                  this.getPushMessages();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHMESSAGESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHMESSAGESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.pushMessagesDataService.updatePushMessages(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PUSHMESSAGESUPDATED')});
                if (proceed === j) {
                  this.getPushMessages();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHMESSAGESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHMESSAGESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.pushMessagesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('pushMessages.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPushMessages() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Push Messages?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.pushMessagesDataService.deletePushMessages(this.selectedPushMessages).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Push Messages Deleted', life: 3000});
            this.getPushMessages();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Messages not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Messages not deleted'});
        });
        this.selectedPushMessages = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.pushMessages, this.columns, 'PushMessages');
  }

  exportExcel() {
    ToolsService.exportExcel(this.pushMessages, this.columns, 'PushMessages');
  }

  exportCsv() {
    ToolsService.exportCsv(this.pushMessages, this.columns, 'PushMessages');
  }

  refresh() {
    this.pushMessagesDataService.clearCache();
    this.getPushMessages();
  }

  pushMessageAdd(): void {
    if (!this.navigation.getParamValue('pushMessages.addInDialog', true)) {
      this.router.navigate(['/push-messages/add']);
      return;
    }
    this.pushMessageAddDialog = this.dialogService.open(DialogPushMessageFormComponent, {
      header: '', // this.translate.instant('NEWPUSHMESSAGE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        pushMessage: {id: null},
        pushDevices: this.pushMessagesColumnsService.pushDevices,
        pushNotifications: this.pushMessagesColumnsService.pushNotifications,
        users: this.pushMessagesColumnsService.users,
      },
    });

    this.pushMessageAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'PushMessage added'});
        this.getPushMessages();
      }
    });
  }

  pushMessageEdit(pushMessage: PushMessage): void {
    if (!this.navigation.getParamValue('pushMessages.editInDialog', true)) {
      this.router.navigate(['/push-messages/edit/' + pushMessage.id]);
      return;
    }
    this.pushMessageEditDialog = this.dialogService.open(DialogPushMessageFormComponent, {
      header: '', // this.translate.instant('EDITPUSHMESSAGE') + ' ' + pushMessage.languageCode,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        pushMessage: pushMessage,
        pushDevices: this.pushMessagesColumnsService.pushDevices,
        pushNotifications: this.pushMessagesColumnsService.pushNotifications,
        users: this.pushMessagesColumnsService.users,
      },
    });

    this.pushMessageEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'PushMessage updated'});
        this.getPushMessages();
      }
    });
  }

  pushMessageDetails(pushMessage: PushMessage): void {
    if (!this.navigation.getParamValue('pushMessages.detailsInDialog', true)) {
      this.router.navigate(['/push-messages/details/' + pushMessage.id]);
      return;
    }
    this.pushMessageDetailsDialog = this.dialogService.open(DialogPushMessageDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        pushMessage: pushMessage,
      }
    });
  }

  pushMessageDelete(pushMessage: PushMessage): void {
    this.pushMessageToDelete = pushMessage;
    this.messageService.clear();
    this.messageService.add({
      key: 'pushMessageDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + pushMessage.languageCode + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.pushMessagesDataService.deletePushMessage(this.pushMessageToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Push Message deleted'});
      this.getPushMessages();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Message not deleted'});
    });
  }

  onDeleteReject(): void {
    this.pushMessageToDelete = {};
    this.messageService.clear('pushMessageDelete');
  }

}
